import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import useCurrencyStore, { CURRENCIES, formatPrice, sortedCurrencies } from '../store/useCurrencyStore';
import api from '../services/api';
import useToastStore from '../store/useToastStore';

const TX_STATUS_STYLES = {
  COMPLETED: 'text-green-400 bg-green-400/10',
  PENDING: 'text-yellow-400 bg-yellow-400/10',
  FAILED: 'text-red-400 bg-red-400/10',
  REFUNDED: 'text-blue-400 bg-blue-400/10',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function WalletPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isProvider = user?.accountType === 'PROVIDER';
  const { currency, localCode, setCurrency } = useCurrencyStore();

  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);

  // Use shared sorted currencies (local first, USD last)
  const pickerCurrencies = sortedCurrencies(localCode);

  useEffect(() => {
    const fetchWallet = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/wallet/balance');
        setWalletData(data);
      } catch {
        setError('Could not load wallet data.');
      }
      setLoading(false);
    };
    fetchWallet();
  }, []);

  const handleComingSoon = (label) => {
    useToastStore.getState().addToast(`${label} — Coming soon!`, 'info');
  };

  const balance = walletData?.balance ?? 0;
  const pending = walletData?.pending ?? walletData?.pendingAmount ?? 0;
  const transactions = walletData?.transactions || [];

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-dark-surface border border-dark-border"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-dark-text">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>Wallet</h1>
      </div>

      <div className="px-4 flex flex-col gap-5">

        {loading ? (
          <div className="flex flex-col gap-4">
            <div className="h-44 bg-dark-surface rounded-3xl animate-pulse" />
            <div className="h-12 bg-dark-surface rounded-2xl animate-pulse" />
            <div className="h-12 bg-dark-surface rounded-2xl animate-pulse" />
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl px-4 py-5 text-center">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        ) : (
          <>
            {/* Balance card */}
            <div className="relative bg-brand-gradient rounded-3xl p-6 overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-white/5 rounded-full" />

              <div className="flex items-center justify-between mb-1 relative z-10">
                <p className="text-white/70 text-sm font-medium">
                  {isProvider ? 'Available Earnings' : 'Wallet Balance'}
                </p>
                {/* Currency selector pill */}
                <button
                  onClick={() => setShowCurrencyPicker(v => !v)}
                  className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 transition-colors px-3 py-1 rounded-full"
                >
                  <span className="text-sm">{currency.flag}</span>
                  <span className="text-white text-xs font-semibold">{currency.code}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>

              <p className="text-white text-4xl font-bold relative z-10" style={{ letterSpacing: '-1px' }}>
                {formatPrice(balance, currency)}
              </p>
              {pending > 0 && (
                <div className="flex items-center gap-1.5 mt-3 relative z-10">
                  <div className="w-2 h-2 rounded-full bg-yellow-300" />
                  <p className="text-white/70 text-sm">
                    {formatPrice(pending, currency)} pending
                  </p>
                </div>
              )}
              {isProvider && (
                <p className="text-white/50 text-xs mt-4 relative z-10">After 15% platform commission</p>
              )}
            </div>

            {/* Currency picker dropdown */}
            {showCurrencyPicker && (
              <div className="bg-dark-surface border border-dark-border rounded-2xl overflow-hidden -mt-2">
                <p className="text-dark-subtext text-xs font-medium uppercase tracking-wide px-4 pt-3 pb-2">
                  Select Currency
                </p>
                {pickerCurrencies.map(c => (
                  <button
                    key={c.code}
                    onClick={() => { setCurrency(c.code); setShowCurrencyPicker(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-t border-dark-border transition-colors ${
                      currency.code === c.code
                        ? 'bg-brand-purple/10'
                        : 'hover:bg-dark-muted/30'
                    }`}
                  >
                    <span className="text-xl">{c.flag}</span>
                    <div className="flex-1">
                      <p className="text-dark-text text-sm font-medium">{c.label}</p>
                      <p className="text-dark-subtext text-xs">{c.code}</p>
                    </div>
                    {currency.code === c.code && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Action buttons */}
            {isProvider ? (
              <div className="flex gap-3">
                <button
                  onClick={() => handleComingSoon('Withdraw')}
                  className="flex-1 py-3.5 rounded-2xl bg-brand-gradient text-white font-semibold text-sm"
                >
                  Withdraw
                </button>
                <button
                  onClick={() => handleComingSoon('Add Payment Method')}
                  className="flex-1 py-3.5 rounded-2xl bg-dark-surface border border-dark-border text-dark-text font-semibold text-sm"
                >
                  Payment Method
                </button>
              </div>
            ) : (
              /* CLIENT: Top Up stub card */
              <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-dark-text font-semibold text-sm">Top Up Balance</p>
                    <p className="text-dark-subtext text-xs mt-0.5">Add funds to pay for services</p>
                  </div>
                  <span className="text-2xl">💳</span>
                </div>
                <button
                  onClick={() => handleComingSoon('Top Up')}
                  className="w-full py-3 rounded-xl bg-brand-gradient text-white text-sm font-semibold"
                >
                  Top Up
                </button>
              </div>
            )}

            {/* Add Payment Method (providers see this as secondary) */}
            {!isProvider && (
              <button
                onClick={() => handleComingSoon('Add Payment Method')}
                className="w-full py-3.5 rounded-2xl bg-dark-surface border border-dashed border-dark-border text-dark-subtext font-medium text-sm flex items-center justify-center gap-2"
              >
                <span className="text-lg">➕</span>
                Add Payment Method
              </button>
            )}

            {/* Transactions */}
            <div>
              <p className="text-dark-subtext text-xs font-medium uppercase tracking-wide mb-3">
                Transaction History
              </p>

              {transactions.length === 0 ? (
                <div className="bg-dark-surface border border-dark-border rounded-2xl flex flex-col items-center justify-center py-10">
                  <p className="text-3xl mb-2">💸</p>
                  <p className="text-dark-text font-semibold text-sm mb-1">No transactions yet</p>
                  <p className="text-dark-subtext text-xs">Your transaction history will appear here</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {transactions.map((tx, i) => (
                    <div
                      key={tx.id || i}
                      className="bg-dark-surface border border-dark-border rounded-2xl px-4 py-3.5 flex items-center gap-3"
                    >
                      {/* Type indicator */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg ${
                        tx.type === 'CREDIT'
                          ? 'bg-green-500/10'
                          : 'bg-red-500/10'
                      }`}>
                        {tx.type === 'CREDIT' ? '⬇️' : '⬆️'}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-dark-text text-sm font-medium truncate">
                          {tx.description || (tx.type === 'CREDIT' ? 'Payment received' : 'Payment sent')}
                        </p>
                        <p className="text-dark-subtext text-xs mt-0.5">
                          {timeAgo(tx.createdAt)}
                        </p>
                      </div>

                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <p className={`text-sm font-bold ${
                          tx.type === 'CREDIT' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {tx.type === 'CREDIT' ? '+' : '-'}{formatPrice(tx.amount, currency)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          TX_STATUS_STYLES[tx.status] || 'text-dark-subtext bg-dark-muted'
                        }`}>
                          {tx.status ? (tx.status.charAt(0) + tx.status.slice(1).toLowerCase()) : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Commission note */}
            <div className="bg-dark-surface border border-dark-border rounded-2xl px-4 py-3 flex items-start gap-2.5">
              <span className="text-base mt-0.5">ℹ️</span>
              <p className="text-dark-subtext text-xs leading-relaxed">
                Findr takes a <span className="text-dark-text font-semibold">15% platform commission</span> on all completed transactions. This helps us maintain the platform and protect both clients and providers.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
